import React, { useState, useEffect } from "react";

const NAV_TABS = [
  { id: "home", icon: "🏠", label: "Home" },
  { id: "school", icon: "🏫", label: "School & Activities" },
  { id: "links", icon: "🔗", label: "Quick Links" },
  { id: "essential", icon: "🔒", label: "Essential Info" },
];

function LinkSheet({ link, onClose, onSave, saving }) {
  const [title, setTitle] = useState(link?.name || "");
  const [url, setUrl] = useState(link?.url || "");
  const [notes, setNotes] = useState(link?.notes || "");
  const [error, setError] = useState("");

  const handleSave = () => {
    if (!title.trim()) { setError("Title is required"); return; }
    if (!url.trim()) { setError("URL is required"); return; }
    const finalUrl = url.startsWith("http") ? url : `https://${url}`;
    onSave({ id: link?.id, name: title.trim(), url: finalUrl, notes: notes.trim() });
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }} />
      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderRadius: "20px 20px 0 0", zIndex: 201, paddingBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} /></div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 20px 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={onClose} style={sheetBtn}>Cancel</button>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 600, color: "var(--charcoal)" }}>{link ? "Edit Link" : "Add Link"}</span>
          <button onClick={handleSave} disabled={saving} style={{ ...sheetBtn, color: "var(--sage)", fontWeight: 800, opacity: saving ? 0.5 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        </div>
        {error && <div style={{ padding: "8px 20px", color: "var(--rose)", fontSize: 13, fontWeight: 600 }}>{error}</div>}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={lbl}>Title</div>
          <input autoFocus value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Family Shared Drive"
            style={{ width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }} />
        </div>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div style={lbl}>Link</div>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://drive.google.com/..."
            style={{ width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }} />
        </div>
        <div style={{ padding: "16px 20px" }}>
          <div style={lbl}>Notes (optional)</div>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="What's this for?"
            style={{ width: "100%", border: "none", outline: "none", fontSize: 15, fontWeight: 500, color: "var(--charcoal)", fontFamily: "inherit", background: "transparent" }} />
        </div>
      </div>
    </>
  );
}

export default function DriveLinksPage({ onBack, onTabTap, readOnly = false }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/.netlify/functions/notion?type=driveLinks")
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(typeof d.error === "string" ? d.error : JSON.stringify(d.error));
        else setLinks(d.items || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (link) => {
    setSaving(true);
    try {
      if (link.id) {
        const res = await fetch("/.netlify/functions/notion", {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pageId: link.id, action: "updateDriveLink", value: { name: link.name, url: link.url, notes: link.notes } }),
        });
        if (!res.ok) throw new Error("Failed to update link");
        setLinks(prev => prev.map(l => l.id === link.id ? { ...l, ...link } : l));
      } else {
        const nextSortOrder = links.length ? Math.max(...links.map(l => l.sortOrder || 0)) + 1 : 1;
        const res = await fetch("/.netlify/functions/notion", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "createDriveLink", value: { name: link.name, url: link.url, notes: link.notes, sortOrder: nextSortOrder } }),
        });
        const data = await res.json();
        if (!res.ok || !data.item) throw new Error("Failed to create link");
        setLinks(prev => [...prev, data.item]);
      }
      setShowSheet(false);
      setEditingLink(null);
    } catch (e) {
      setError(e.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    setLinks(prev => prev.filter(l => l.id !== id));
    await fetch("/.netlify/functions/notion", {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pageId: id, action: "delete" }),
    });
  };

  return (
    <>
      <div className="app">
        <header style={{ background: "var(--white)", padding: "54px 20px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onBack} style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--cream-hover)", border: "none", fontSize: 18, color: "var(--mid)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>‹</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 600, color: "var(--charcoal)" }}>📂 Drive Links</div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>Tap to open · shared across the family</div>
          </div>
          {!readOnly && <button onClick={() => { setEditingLink(null); setShowSheet(true); }} style={{ padding: "8px 16px", borderRadius: 20, background: "var(--sage)", border: "none", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>+ Add</button>}
        </header>

        <div className="scroll-body" style={{ padding: "16px" }}>
          {loading && <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading…</div>}
          {error && <div style={{ padding: 20, color: "var(--rose)", fontSize: 13, background: "var(--cream-hover)", borderRadius: 10, marginBottom: 12 }}>Error: {error}</div>}
          {!loading && !error && links.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
              <div style={{ fontSize: 15, color: "var(--muted)", fontWeight: 600, marginBottom: 8 }}>No links yet</div>
              <div style={{ fontSize: 13, color: "var(--muted)" }}>Add Google Drive folders, docs, or sheets you access often.</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {links.map(link => (
                <div key={link.id} style={{ display: "flex", gap: 10, alignItems: "stretch" }}>
                  <a href={link.url} target="_blank" rel="noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: "var(--white)", borderRadius: 14, border: "1px solid var(--border)", textDecoration: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", minWidth: 0 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 11, background: "#E8F0FE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>📂</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--charcoal)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{link.name}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{link.notes || link.url}</div>
                    </div>
                    <span style={{ color: "var(--sage)", fontSize: 18, flexShrink: 0 }}>↗</span>
                  </a>
                  {!readOnly && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <button onClick={() => { setEditingLink(link); setShowSheet(true); }} style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border)", background: "var(--white)", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✏️</button>
                      <button onClick={() => handleDelete(link.id)} style={{ width: 40, height: 40, borderRadius: 10, border: "1px solid var(--border)", background: "var(--white)", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--rose)" }}>🗑️</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <nav style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: "var(--app-max-width)", background: "var(--white)", borderTop: "1px solid var(--border)", display: "flex", padding: "10px 0 20px", zIndex: 100 }}>
          {NAV_TABS.map(t => (
            <div key={t.id} onClick={() => onTabTap?.(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, cursor: "pointer", padding: "4px 0" }}>
              <div style={{ fontSize: 22 }}>{t.icon}</div>
              <div style={{ fontSize: 10, fontWeight: t.id === "links" ? 800 : 700, color: t.id === "links" ? "var(--sage)" : "var(--muted)", textAlign: "center", lineHeight: 1.2 }}>{t.label}</div>
              {t.id === "links" && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--sage)", marginTop: 1 }} />}
            </div>
          ))}
        </nav>
      </div>

      {showSheet && (
        <LinkSheet
          link={editingLink}
          saving={saving}
          onClose={() => { setShowSheet(false); setEditingLink(null); }}
          onSave={handleSave}
        />
      )}
    </>
  );
}

const sheetBtn = { background: "none", border: "none", fontSize: 14, fontWeight: 700, color: "var(--muted)", cursor: "pointer", fontFamily: "inherit" };
const lbl = { fontSize: 11, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 };
